<template>
    <editor-page class="page-review" title="Page Review" v-if="cmsPage" no-padding>
        <template v-if="!loading">
            <b-navbar class="editor-menu bg-white">
                <b-nav>
                </b-nav>
                <b-nav class="mx-auto">

                    <b-btn-group class="bg-light rounded d-inline-flex align-items-center">
                        <b-dropdown size="sm" id="website-menu" variant="light" no-caret>
                            <template #button-content>
                                <b-img width="24" height="24" style="margin: -4px 0 -2px -2px;"
                                       v-if="cmsWebsite.design.favicon.src" :src="cmsWebsite.design.favicon.src"/>
                                <i v-else class="fas fa-globe"/>
                                <span class="ml-2" v-if="isExtraLargeLayout">{{ cmsWebsite.name|tr }}</span>
                            </template>
                        </b-dropdown>

                        <b-dropdown size="sm" variant="light" class="border-left" no-caret>
                            <template #button-content>
                                <i v-if="isExtraLargeLayout" class="fas mr-2" :class="cmsPage.glyph || 'fa-file-lines'"/>
                                <span>{{ cmsPage.name|tr }}</span>
                            </template>
                        </b-dropdown>
                    </b-btn-group>

                    <b-btn-group class="ml-4">
                        <e-btn-language size="sm" variant="light" @input="toggleLanguage" :value="displayLanguage" show-label/>
                    </b-btn-group>
                </b-nav>
            </b-navbar>
        </template>

        <b-tabs>
            <b-tab title="Text Review">
                <e-container class="text-left">
                    <h4>Page Meta</h4>
                    <b-media>
                        <template #aside>
                            <b-img :src="cmsPage.meta.socialImage" class="rounded" blank-color="#777" style="min-width: 80px; max-width: 200px; min-height: 80px; max-height: 160px;" fluid-grow/>
                            <br>
                            <e-status-badge status="error" v-if="!cmsPage.meta.socialImage">Missing Page Image</e-status-badge>
                        </template>
                        <h5>{{cmsPage.meta.title|tr}}
                            <e-status-badge status="error" v-if="!tr(cmsPage.meta.title)">Missing Page Title</e-status-badge>
                        </h5>
                        <p>{{cmsPage.meta.description|tr}}
                            <e-status-badge status="error" v-if="!tr(cmsPage.meta.description)">Missing Page Description</e-status-badge>
                        </p>
                    </b-media>

                    <hr>

                    <h4>Checklist</h4>
                    <p>
                        <b>{{review.stats.h1Count}} H1</b>
                        <e-status-badge status="error" v-if="review.stats.h1Count !== 1">Invalid H1 count</e-status-badge>
                    </p>

                    <hr>

                    <h4>Page Content</h4>
                    <div class="bg-white p-2 rounded">
                        <review-block v-for="block in cmsPage.blocks" :block="block" :key="block.id"/>
                    </div>
                </e-container>
            </b-tab>
            <b-tab v-if="review" :title="`${review.stats.errors} Errors and ${review.stats.warnings} Warnings`">
                <e-table-helper class="text-left" :items="review.errors" :fields="errorFields">

                </e-table-helper>
            </b-tab>
        </b-tabs>
    </editor-page>
</template>

<script>
import CmsBlockFooter from "@/helpers/editor/blocks/CmsBlockFooter.js";
import CmsBlockMenu from "@/helpers/editor/blocks/CmsBlockMenu.js";
import PageChecker from "@/helpers/review/PageChecker.js";
import EditorPage from "@/layout/editor-page.vue";
import ReviewBlock from "@components/review/review-block.vue";
import VirtualAssistantAvatar from "@components/virtual-assistant-avatar.vue";
import EButtonConfirm from "@vue-components/components/e-button-confirm.vue";
import EBtnLanguage from "@vue-components/components/e-btn-language.vue";
import ECheckMark from "@vue-components/components/e-check-mark.vue";
import EContainer from "@vue-components/components/e-container.vue";
import EStatusBadge from "@vue-components/components/e-status-badge.vue";
import ETableHelper from "@vue-components/components/e-table-helper.vue";
import NetworkV2 from "@vue-components/helpers/NetworkV2.js";
// import EResponsivePreview from "@vue-components/components/e-responsive-preview.vue";
import Permission from "@vue-components/helpers/permissions";

// page-review #hot-reload-debug
export default {
    name: `page-review`,
    components: {
        EButtonConfirm, ECheckMark, EBtnLanguage, VirtualAssistantAvatar,
        EStatusBadge,
        ETableHelper,
        EContainer,
        ReviewBlock,
        EditorPage
    },
    data() {
        return {
            Permission,
            loading: false,
            MODES: {
                STRUCT: `struct`,
                TRANSLATION: `translation`
            },
            mode: `struct`,
            review: null,
            websiteEdited: false,
            errorFields: [
                {key: `message`, label: `Message`},
                {key: `name`, label: `Name`},
                {key: `status`, label: `Status`}
            ]
        }
    },
    computed: {
        shortcuts() {
            return {
                // "ctrl+s": this.save,
            }
        }
    },
    async activated() {
        await this.init();
    },
    async mounted() {
        await this.init();
    },
    methods: {
        async init() {
            if (this.loading) {
                return;
            }

            this.loading = true;
            this.showLoading();

            NetworkV2.get(`/api/page/${this.$route.params.pageId}`)
                .then(resp => {
                    this.$store.commit(`setPage`, {page: resp.data, route: this.$route});

                    if (!this.cmsWebsite.menu.block) {
                        // console.warn(`Tmp always removing previous header`)
                        let menu = new CmsBlockMenu();
                        menu.logo.src = this.cmsWebsite.design.logo.src;
                        const darkTheme = this.cmsWebsite.getColorScheme(`dark`);
                        if (darkTheme) {
                            menu.colorScheme = darkTheme;
                            menu.logo.filter = `recolor-white`;
                        }
                        this.$set(this.cmsWebsite.menu, `block`, menu);
                    }
                    if (!this.cmsWebsite.footer.block) {
                        // console.warn(`Tmp always removing previous footer`)
                        let footer = new CmsBlockFooter();
                        footer.logo.src = this.cmsWebsite.design.logo.src;
                        const darkTheme = this.cmsWebsite.getColorScheme(`dark`);
                        if (darkTheme) {
                            footer.colorScheme = darkTheme;
                            footer.logo.filter = `recolor-white`;
                        }
                        this.$set(this.cmsWebsite.footer, `block`, footer);
                    }
                    this.$forceUpdate();
                    this.hideLoading();
                    this.loading = false;
                    this.updateReview();
                })
                .catch(error => {
                    console.error(error);
                    this.loading = false;
                })
        },
        // onPageEdited() {
        //     this.save(() => {
        //         this.$store.dispatch(`refreshWebsite`);
        //     })
        // },
        // async save(callback) {
        //     try {
        //         this.showLoading();
        //
        //         // if (this.websiteEdited) {
        //         if (this.canWrite(Permission.CMS.feature.WEBSITES)) {
        //             const websiteSave = this.cmsWebsite.toJSON();
        //             await NetworkV2.post(`/api/websites/edit`, websiteSave);
        //         } else {
        //             console.warn(`Not allowed to edit website settings`);
        //         }
        //         // }
        //
        //         const pageSave = this.cmsPage.toJSON();
        //         await NetworkV2.post(`/api/pages/edit`, pageSave);
        //         this.hideLoading();
        //         this.showToast(`Page Saved|Page enregistrée`, undefined, `b-toaster-bottom-right`);
        //         if (callback && typeof callback === `function`) {
        //             callback();
        //         }
        //     } catch (e) {
        //         console.error(e);
        //         this.showErrorMessage(`Not allowed|Non autorisé`, `You don't permission to do this action|Vous n'avez pas la permission de faire cette action`);
        //     }
        // },
        toggleLanguage(event) {
            // event is now a language object from btn-language
            this.$store.state.previewLanguage = event;
            this.updateReview();
        },
        updateReview() {
            this.review = new PageChecker(this.cmsWebsite, this.cmsPage, this.displayLanguage);
        }
    }
}
</script>

<style lang="scss">
@import "../assets/style/editor/editor";

.editor-page-container {
    .editor-page {
        --color-accent: #005fe5;
        min-height: 100vh;
        overflow: auto !important;
    }
}
</style>
